import { API } from "aws-amplify"
import { isLocalHost, isLocalNoAwsMode } from "@helpers/runtimeMode"
import {
  adminsApiGet,
  adminsApiPut,
  adminsApiPost,
  adminsApiDel
} from "@helpers/adminsApiClient"
import { handleApiAuthError } from "@helpers/authSession"
import { devLog } from "@helpers/devLog"

/**
 * Customer Souq API Service
 * Handles all customer souq related API calls for admin dashboard
 */
class CustomerSouqApiService {
  constructor() {
    // Check if running locally (localhost)
    const isLocal = isLocalHost()
    // For local, use souqApiGatewayLocal which points to localhost:3010
    // For production, use adminsApi which points to madmin.caraqarapp.qa (the correct domain)
    this.apiName = isLocal ? 'souqApiGatewayLocal' : 'adminsApi'
    // Both local and production use the same path structure
    this.baseUrl = '/commons/souq/v1/admin/customer'
    
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      devLog("CustomerSouqApiService initialized:", {
        isLocal,
        apiName: this.apiName,
        baseUrl: this.baseUrl,
        hostname: window.location.hostname
      })
    }
  }

  shouldSkipProtectedCalls() {
    const hasMigrationApi = Boolean(
      process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_V2_BACKEND_URL
    )
    return isLocalNoAwsMode() && !hasMigrationApi
  }

  useV2Api() {
    return Boolean(
      process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_V2_BACKEND_URL
    )
  }

  async apiGet(path, extraHeaders = {}) {
    const endpoint = path.startsWith(this.baseUrl) ? path : `${this.baseUrl}${path}`
    if (this.useV2Api()) {
      return adminsApiGet(endpoint, { headers: extraHeaders })
    }
    return API.get(this.apiName, endpoint, { headers: extraHeaders })
  }

  async apiPut(path, body, extraHeaders = {}) {
    const endpoint = path.startsWith(this.baseUrl) ? path : `${this.baseUrl}${path}`
    if (this.useV2Api()) {
      return adminsApiPut(endpoint, body, { headers: extraHeaders })
    }
    return API.put(this.apiName, endpoint, { body, headers: extraHeaders })
  }

  async apiPost(path, body, extraHeaders = {}) {
    const endpoint = path.startsWith(this.baseUrl) ? path : `${this.baseUrl}${path}`
    if (this.useV2Api()) {
      return adminsApiPost(endpoint, body, { headers: extraHeaders })
    }
    return API.post(this.apiName, endpoint, { body, headers: extraHeaders })
  }

  async apiDel(path, extraHeaders = {}) {
    const endpoint = path.startsWith(this.baseUrl) ? path : `${this.baseUrl}${path}`
    if (this.useV2Api()) {
      return adminsApiDel(endpoint, { headers: extraHeaders })
    }
    return API.del(this.apiName, endpoint, { headers: extraHeaders })
  }

  handleAuthError(error) {
    handleApiAuthError(error)
  }

  noCacheHeaders() {
    return { "Cache-Control": "no-cache", Pragma: "no-cache" }
  }

  /**
   * Get pending customer souq items
   */
  async getPendingItems(params = {}) {
    if (this.shouldSkipProtectedCalls()) {
      return { data: [], pagination: { page: 1, limit: 0, total: 0 } }
    }
    try {
      const query = new URLSearchParams({
        ...(params.type ? { type: params.type } : {}),
        ...(params.page ? { page: String(params.page) } : {}),
        ...(params.limit ? { limit: String(params.limit) } : {}),
        _: String(Date.now())
      }).toString()
      const endpoint = `${this.baseUrl}/pending-items${query ? `?${query}` : ''}`
      
      devLog('API: Fetching pending items:', {
        apiName: this.apiName,
        endpoint,
        fullUrl: `${this.apiName}:${endpoint}`
      })
      
      const result = await this.apiGet(endpoint, this.noCacheHeaders())
      return result
    } catch (error) {
      this.handleAuthError(error)
      return { data: [], pagination: { page: 1, limit: 0, total: 0 } }
    }
  }

  /**
   * Get new customer souq items
   */
  async getNewItems(params = {}) {
    if (this.shouldSkipProtectedCalls()) {
      return { data: [], pagination: { page: 1, limit: 0, total: 0 } }
    }
    try {
      const query = new URLSearchParams({
        ...(params.type ? { type: params.type } : {}),
        ...(params.page ? { page: String(params.page) } : {}),
        ...(params.limit ? { limit: String(params.limit) } : {}),
        _: String(Date.now())
      }).toString()
      const endpoint = `${this.baseUrl}/new-items${query ? `?${query}` : ''}`
      
      devLog('API: Fetching new items:', {
        apiName: this.apiName,
        endpoint,
        fullUrl: `${this.apiName}:${endpoint}`
      })
      
      const result = await this.apiGet(endpoint, this.noCacheHeaders())
      return result
    } catch (error) {
      this.handleAuthError(error)
      return { data: [], pagination: { page: 1, limit: 0, total: 0 } }
    }
  }

  /**
   * Get customer souq item statistics
   */
  async getStats() {
    try {
      return await this.apiGet("/stats")
    } catch (error) {
      this.handleAuthError(error)
      console.error('Error fetching stats:', error)
      throw error
    }
  }

  /**
   * Get count of new customer items
   */
  async getNewItemsCount() {
    if (this.shouldSkipProtectedCalls()) return { count: 0 }
    try {
      return await this.apiGet(`/new-items/count?_=${Date.now()}`, this.noCacheHeaders())
    } catch (error) {
      this.handleAuthError(error)
      return { count: 0 }
    }
  }

  /**
   * Get count of pending customer items
   */
  async getPendingItemsCount() {
    if (this.shouldSkipProtectedCalls()) return { count: 0 }
    try {
      return await this.apiGet(`/pending-items/count?_=${Date.now()}`, this.noCacheHeaders())
    } catch (error) {
      this.handleAuthError(error)
      return { count: 0 }
    }
  }

  /**
   * Get specific customer souq item details
   */
  async getItemDetails(id) {
    try {
      return await this.apiGet(`/${id}`)
    } catch (error) {
      this.handleAuthError(error)
      console.error('Error fetching item details:', error)
      throw error
    }
  }

  /**
   * Approve a customer souq item
   */
  async approveItem(id, data = {}) {
    try {
      const endpoint = `${this.baseUrl}/${id}/approve`
      return await this.apiPut(endpoint, {
        adminNotes: data.adminNotes || "",
        approvedBy: data.approvedBy || "admin",
        ...data
      })
    } catch (error) {
      this.handleAuthError(error)
      console.error('Error approving item:', error)
      throw error
    }
  }

  /**
   * Reject a customer souq item
   */
  async rejectItem(id, data = {}) {
    try {
      const endpoint = `${this.baseUrl}/${id}/reject`
      return await this.apiPut(endpoint, {
        adminNotes: data.adminNotes || "",
        rejectedBy: data.rejectedBy || "admin",
        rejectionReason: data.rejectionReason || "",
        ...data
      })
    } catch (error) {
      this.handleAuthError(error)
      console.error('Error rejecting item:', error)
      throw error
    }
  }

  /**
   * Change customer souq item status
   */
  async changeItemStatus(id, status, reason = '', sk) {
    if (this.shouldSkipProtectedCalls()) return { success: true }
    try {
      if (status === "APPROVED" || status === "ACTIVE") {
        return await this.approveItem(id, { ...(reason ? { adminNotes: reason } : {}), ...(sk ? { sk } : {}) })
      }
      if (status === "REJECTED") {
        return await this.rejectItem(id, { rejectionReason: reason || "", ...(sk ? { sk } : {}) })
      }
      // Backend contract currently supports approve/reject for admin customer items.
      return { success: true, skipped: true }
    } catch (error) {
      this.handleAuthError(error)
      console.error('Error changing item status:', error)
      throw error
    }
  }

  /**
   * Publish a customer souq item
   */
  async publishItem(id, data = {}) {
    if (this.shouldSkipProtectedCalls()) return { success: true }
    // Publishing customer items in admin backend is performed via approve endpoint.
    return this.approveItem(id, {
      adminNotes: data.adminNotes || "",
      approvedBy: data.publishedBy || "admin",
      ...(data.sk ? { sk: data.sk } : {})
    })
  }

  /**
   * Update customer souq item
   */
  async updateItem(id, data) {
    if (this.shouldSkipProtectedCalls()) return { success: true }
    // No dedicated admin update endpoint exists for customer items in current backend.
    // Keep API call surface non-breaking and return a benign response.
    return { success: true, skipped: true }
  }

  /**
   * Delete customer souq item
   */
  async deleteItem(id) {
    try {
      // Try new endpoint first
      try {
        await this.apiDel(`/${id}`)
        return { success: true }
      } catch (err) {
        const status = err?.response?.status || err?.status
        if (status !== 404) throw err
        await this.apiDel(`/items/${id}`)
        return { success: true }
      }
    } catch (error) {
      this.handleAuthError(error)
      console.error('Error deleting item:', error)
      throw error
    }
  }

  /**
   * Bulk approve items
   */
  async bulkApproveItems(ids, data = {}) {
    try {
      return await this.apiPost("/bulk/approve", {
        itemIds: ids,
        adminNotes: data.adminNotes || "",
        approvedBy: data.approvedBy || "admin",
        ...data
      })
    } catch (error) {
      this.handleAuthError(error)
      console.error('Error bulk approving items:', error)
      throw error
    }
  }

  /**
   * Bulk reject items
   */
  async bulkRejectItems(ids, data = {}) {
    try {
      return await this.apiPost("/bulk/reject", {
        itemIds: ids,
        rejectionReason: data.rejectionReason || "",
        rejectedBy: data.rejectedBy || "admin",
        ...data
      })
    } catch (error) {
      this.handleAuthError(error)
      console.error('Error bulk rejecting items:', error)
      throw error
    }
  }

  /**
   * Search customer souq items
   */
  async searchItems(query, filters = {}) {
    try {
      return await this.apiPost("/search", { query, filters })
    } catch (error) {
      this.handleAuthError(error)
      console.error('Error searching items:', error)
      throw error
    }
  }

  /**
   * Get customer souq item analytics
   */
  async getAnalytics(timeRange = '30d') {
    try {
      return await this.apiGet(`/analytics?timeRange=${timeRange}`)
    } catch (error) {
      this.handleAuthError(error)
      console.error('Error fetching analytics:', error)
      throw error
    }
  }
}

export default new CustomerSouqApiService()

